import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import PersonalInfoSection from './sections/PersonalInfoSection';
import LoginInfoSection from './sections/LoginInfoSection';
import ConsentSection from './sections/ConsentSection';
import ChangePasswordModal from '../ChangePasswordModal';
import WithdrawAccountModal from '../WithdrawAccountModal';
import { useAuthStore } from '../../../store/authStore';
import type { ProfileFormData } from '../../../types';

export interface ProfileFormProps {
  initialData?: ProfileFormData;
  onSubmit: (data: ProfileFormData) => Promise<void>;
  isLoading: boolean;
  hidePhoneVerification?: boolean;
  influencerId?: string;
}

const DEFAULT_FORM_DATA: ProfileFormData = {
  name: '',
  nickname: '',
  phoneNumber: '',
  phoneVerified: false,
  marketingConsentCollection: false,
  marketingConsentPromotion: false,
  marketingConsentEmail: false,
  marketingConsentSMS: false,
};

const ProfileForm: React.FC<ProfileFormProps> = ({
  initialData,
  onSubmit,
  isLoading,
  hidePhoneVerification = false,
  influencerId,
}) => {
  const { t } = useTranslation('profile');
  const { user } = useAuthStore();

  const [formData, setFormData] = useState<ProfileFormData>(
    initialData ?? DEFAULT_FORM_DATA
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const isPhoneExisting = !!(initialData?.phoneNumber?.trim());

  // Sync form data only on initial load (when initialData transitions from undefined to a value)
  const initialDataLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (initialData && !initialDataLoadedRef.current) {
      initialDataLoadedRef.current = true;
      setFormData(initialData);
    }
  }, [initialData]);

  const handleChange = useCallback(
    (field: string, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error for the field being edited
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    []
  );

  const validate = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('myProfile.form.nameRequired');
    }

    return newErrors;
  }, [formData.name, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const validationErrors = validate();
      setErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        return;
      }

      setIsSubmitting(true);
      try {
        await onSubmit(formData);
        toast.success(t('myProfile.form.saveSuccess'));
      } catch {
        toast.error(t('myProfile.form.saveFailed'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, onSubmit, validate, t]
  );

  const submitting = isSubmitting || isLoading;

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="space-y-8 max-w-3xl mx-auto px-4 py-6"
      >
        {/* Personal Information */}
        <PersonalInfoSection
          name={formData.name}
          nickname={formData.nickname}
          phoneNumber={formData.phoneNumber}
          phoneVerified={formData.phoneVerified}
          onChange={handleChange}
          errors={errors}
          isPhoneExisting={isPhoneExisting}
          hidePhoneVerification={hidePhoneVerification}
        />

        {/* Login Information */}
        <LoginInfoSection
          email={user?.email ?? ''}
          onChangePassword={() => setIsPasswordModalOpen(true)}
        />

        {/* Consent to Receive */}
        <ConsentSection
          marketingConsentCollection={formData.marketingConsentCollection}
          marketingConsentPromotion={formData.marketingConsentPromotion}
          marketingConsentEmail={formData.marketingConsentEmail}
          marketingConsentSMS={formData.marketingConsentSMS}
          onChange={handleChange}
        />

        {/* Save button */}
        <div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
          >
            {submitting
              ? t('myProfile.form.saving')
              : t('myProfile.form.save')}
          </button>
        </div>

        {/* Withdraw account */}
        <div className="pt-2 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={() => setIsWithdrawModalOpen(true)}
            className="text-sm font-medium text-gray-500 hover:text-red-600 transition-colors"
          >
            {t('myProfile.withdraw.entry', '회원탈퇴')}
          </button>
        </div>
      </form>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />

      {/* Withdraw Account Modal */}
      {user?.userId && (
        <WithdrawAccountModal
          isOpen={isWithdrawModalOpen}
          onClose={() => setIsWithdrawModalOpen(false)}
          userId={user.userId}
          influencerId={influencerId}
        />
      )}
    </>
  );
};

export default ProfileForm;
